import { Environment, PATH } from '@/constants';
import {
  GENERIC_FALLBACK_ID,
  IProactiveScenario,
  PROACTIVE_SCENARIOS,
} from '@/constants/crispProactive';
import { getIsHiddenChatFromAdmin } from '@/helpers/crisp';
import {
  getCrisp,
  hasFiredAnyInSession,
  hasFiredInSession,
  isSessionOngoing,
  isWebsiteAvailable,
  matchScenarioPath,
  pickRandomMessage,
  showProactiveMessage,
} from '@/helpers/crispProactive';
import { EShipmentType } from '@/pages/Shipments/config';
import { useGetShipmentSearchQuery } from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

/**
 * Trong các scenario `dwell` khớp pathname, chọn cái cụ thể nhất (path dài nhất).
 * Nhờ vậy `/notification-v2/sender-email` ưu tiên #8 thay vì #7; `/tracking-page` exact (#4)
 * không dính sub-path đã có scenario riêng.
 */
export const findActiveDwellScenario = (pathname: string): IProactiveScenario | undefined =>
  PROACTIVE_SCENARIOS.filter((scenario) => scenario.trigger === 'dwell' && matchScenarioPath(pathname, scenario)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];

/**
 * TR-493 — orchestrator proactive Crisp messages (spec.md §6).
 * Mount 1 lần ở `App.tsx` cạnh `useScript()`. No-op khi Crisp không được phép hiện
 * (env Local / chưa auth / mở từ admin) — đồng bộ điều kiện `useScript`.
 */
const useCrispProactive = (): null => {
  const location = useLocation();
  const { pathname } = location;
  const { token, isAuth } = useSelector(tokenSelector);
  const disabled =
    process.env.REACT_APP_ENV === Environment.Local || !isAuth || getIsHiddenChatFromAdmin(token?.scope);

  const crispReadyRef = useRef(false);
  const replyLockRef = useRef(false);
  const replyLockRegisteredRef = useRef(false);
  const isEmptyDashboardRef = useRef(false);

  // Empty-state signal (P0-8, D9): chỉ fetch khi ở /dashboard, perPage tối thiểu.
  // `shipmentCount.total` là tổng độc lập filter → đáng tin cho "chưa có shipment nào".
  const isDashboard = pathname === PATH.DASHBOARD;
  const { data: shipmentData } = useGetShipmentSearchQuery(
    { type: EShipmentType.TRACKED_SHIPMENT, page: 1, perPage: 1 },
    { skip: disabled || !isDashboard },
  );
  const isEmptyDashboard = useMemo(() => {
    const count = shipmentData?.data?.shipmentCount;
    if (!count) return false; // chưa biết → fail-closed (không show empty-state)
    return count.total === 0;
  }, [shipmentData]);
  isEmptyDashboardRef.current = isEmptyDashboard;

  // Guard chain chung (D5). Giữ trong ref để timer luôn gọi closure mới nhất.
  const tryShowScenarioRef = useRef<(scenario: IProactiveScenario) => void>(() => undefined);
  tryShowScenarioRef.current = (scenario: IProactiveScenario) => {
    if (disabled) return;
    if (!crispReadyRef.current) return; // D6: chỉ đánh giá khi Crisp ready
    if (replyLockRef.current) return; // khách đã reply → dừng proactive
    if (hasFiredInSession(scenario.id)) return; // 1 lần / scenario / session
    if (scenario.id === GENERIC_FALLBACK_ID && hasFiredAnyInSession()) return; // #13 chỉ khi chưa có gì
    if (isSessionOngoing()) {
      replyLockRef.current = true; // conversation thật đang diễn ra → khoá luôn
      return;
    }
    if (!isWebsiteAvailable()) return; // P0-6 online-only, fail-closed
    if (scenario.requiresEmptyState && !isEmptyDashboardRef.current) return; // P0-8
    showProactiveMessage(scenario.id, pickRandomMessage(scenario.messages));
  };

  // Mount-once: bắt thời điểm Crisp ready (D6) + đăng ký reply-lock (P0-3).
  useEffect(() => {
    if (disabled) return undefined;

    const registerReplyLock = () => {
      if (replyLockRegisteredRef.current) return; // tránh đăng ký trùng nếu effect re-run
      const crisp = getCrisp();
      if (!crisp) return;
      replyLockRegisteredRef.current = true;
      try {
        crisp.push([
          'on',
          'message:sent',
          () => {
            replyLockRef.current = true;
          },
        ]);
      } catch {
        // ignore — proactive vẫn fail-safe
      }
    };

    const prevTrigger = window.CRISP_READY_TRIGGER;
    window.CRISP_READY_TRIGGER = () => {
      crispReadyRef.current = true;
      registerReplyLock();
      if (typeof prevTrigger === 'function') prevTrigger();
    };

    // Crisp đã sẵn sàng trước khi hook mount (vd remount) → set ready ngay.
    if (getCrisp()) {
      crispReadyRef.current = true;
      registerReplyLock();
    }

    return () => {
      window.CRISP_READY_TRIGGER = prevTrigger;
    };
  }, [disabled]);

  // Dwell theo route (P0-2): timer khớp path; rời path trước khi đủ delay → clear.
  useEffect(() => {
    if (disabled) return undefined;
    const scenario = findActiveDwellScenario(pathname);
    if (!scenario || hasFiredInSession(scenario.id)) return undefined;
    const timer = window.setTimeout(() => tryShowScenarioRef.current(scenario), scenario.delayMs);
    return () => window.clearTimeout(timer);
  }, [pathname, disabled]);

  // Leave-intent (#2, P0-7): chỉ trên /plan, sau khi đã ở lại tối thiểu delayMs (P0-4).
  useEffect(() => {
    if (disabled) return undefined;
    const scenario = PROACTIVE_SCENARIOS.find((s) => s.trigger === 'leaveIntent' && matchScenarioPath(pathname, s));
    if (!scenario || hasFiredInSession(scenario.id)) return undefined;

    let armed = false;
    const armTimer = window.setTimeout(() => {
      armed = true;
    }, scenario.delayMs);
    const handleMouseOut = (event: MouseEvent) => {
      if (armed && event.clientY <= 0) tryShowScenarioRef.current(scenario);
    };
    document.addEventListener('mouseout', handleMouseOut);
    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [pathname, disabled]);

  // Generic fallback (#13, D11): timer session-level ≥90s, độc lập route.
  useEffect(() => {
    if (disabled) return undefined;
    const scenario = PROACTIVE_SCENARIOS.find((s) => s.id === GENERIC_FALLBACK_ID);
    if (!scenario) return undefined;
    const timer = window.setTimeout(() => tryShowScenarioRef.current(scenario), scenario.delayMs);
    return () => window.clearTimeout(timer);
  }, [disabled]);

  return null;
};

export default useCrispProactive;
